import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion, useAnimation } from 'framer-motion';

import OutsideClickHandler from 'react-outside-click-handler';
import PieSlice from '../pie-slice/PieSlice';
import SvgDetailPanel from '../svg-detail-panel/SvgDetailPanel';

import {
    sliceOpt,
    panelOpt,
    calculateValues,
    calculatePanelHeight
} from '../helpers';

import styles from './PieChart.module.scss';
import iconBlueCross from 'assets/images/icon-blue-cross.svg';

const PieChart = ({ sliceData, setVisibility, chartData }) => {
    const sliceControls = useAnimation();
    const panelControls = useAnimation();

    const onOutsideClick = () => {
        sliceControls.start(sliceOpt.stop);
        panelControls.start(panelOpt.stop);
    };

    useEffect(() => {
        if (sliceData && sliceData.length > 0) {
            sliceControls.start(sliceOpt.start);
            panelControls.start(panelOpt.start);
        }
    }, [sliceData]);

    const generatePanels = (sData) => {
        if (!sData) {
            return null;
        }

        const marginBottom = 10;
        const canvasHeight = 300;
        const canvasRightEdge = 600;
        const canvasLeftEdge = 200;

        const corrData = sliceData.find((s) => s.id === 'Cor') || {};
        const corrHeight = calculatePanelHeight('Cor', 40, corrData.options);

        const panelInfo = [
            { id: 'S1', height: 90, width: 196 },
            { id: 'S2', height: 91, width: 196 },
            { id: 'S3', height: 78, width: 196 },
            { id: 'S4', height: 78, width: 200 },
            {
                id: 'Cor',
                height: corrHeight || 40,
                width: 75
            }
        ];

        const idList = sliceData.map((s) => s.id);
        const availablePanels = idList.map((id) => {
            const index = panelInfo.findIndex((p) => p.id === id);
            return panelInfo[index];
        });

        const panelsWithPosY = availablePanels.reduce((p, c, i) => {
            if (i === 0) {
                return [{ ...c, y: 0, x: canvasRightEdge, isRightSide: true }];
            }

            const prev = p[i - 1];
            let y = prev.y + prev.height + marginBottom;
            let x = canvasRightEdge;
            let dy = 0;

            const isRightSide = y + c.height < canvasHeight;
            if (!isRightSide) {
                x = canvasLeftEdge - c.width; // right alignment
                dy = prev.isRightSide
                    ? canvasHeight - c.height - marginBottom
                    : prev.dy - marginBottom - c.height;
            }

            return [
                ...p,
                {
                    ...c,
                    y,
                    x,
                    dy,
                    isRightSide
                }
            ];
        }, []);

        const list = sData.map((s, i) => ({
            ...s,
            meta: panelsWithPosY[i]
        }));
        return list;
    };

    const generateArrows = (sliceData, i) => {
        const { meta, centerAngle } = sliceData;
        const rightEdge = 600;
        const topEdge = 5;
        const leftEdge = 200;
        const bottomEdge = 340;

        const panelY = meta.isRightSide ? meta.y : meta.dy;
        const { x1, y1 } = calculateValues(sliceData, i);
        const panelMY = panelY + meta.height / 2; // panel middle Y position

        const ysp = 10; // y space
        const xsp = 10; // x space
        const isUp = y1 > panelMY;
        const isR = meta.isRightSide;
        const edge = isR ? rightEdge : leftEdge;

        // max edge
        const rxEdge = rightEdge - xsp - xsp * i;
        const lxEdge = leftEdge + xsp + xsp * i;
        const bxEdge = bottomEdge - ysp * i;
        const txEdge = topEdge + ysp * i;

        // min edge
        const rnEdge = rightEdge - xsp - xsp * (5 - (i + 1));
        const bnEdge = bottomEdge - ysp - ysp * (5 - (i + 1));
        const lnEdge = leftEdge + ysp + ysp * (5 - (i + 1));
        const tnEdge = topEdge + ysp + ysp * (5 - (i + 1));

        const getPath0To60 = (x1, y1, panelMY) => {
            const lPath = `L${rxEdge},${bxEdge} L${lxEdge},${bxEdge} L${lxEdge},${panelMY}`;

            return `M${x1},${y1}  L${x1},${txEdge} L${rxEdge},${txEdge} L${rxEdge},${panelMY} ${
                !isR ? lPath : ''
            } L${edge},${panelMY}`;
        };

        const getPath60To120 = (x1, y1, panelMY) => {
            const lPath = `L${rxEdge},${y1} L${rxEdge},${bxEdge} L${lxEdge},${bxEdge} L${lxEdge},${panelMY}`;
            const rPath = ` L${rnEdge},${y1} L${rnEdge},${
                isUp ? panelMY : y1
            } L${rxEdge},${panelMY}`;

            return `M${x1},${y1}  ${!isR ? lPath : rPath} L${edge},${panelMY}`;
        };

        const getPath120To180 = (x1, y1, panelMY) => {
            const lPath = `L${x1},${bxEdge} L${lxEdge},${bxEdge} L${lxEdge},${panelMY}`;
            const rPath = `L${x1},${bnEdge} L${rnEdge},${bnEdge} L${rnEdge},${
                isUp ? panelMY : y1
            } L${rxEdge},${isUp ? panelMY : y1} L${rxEdge},${panelMY}`;

            return `M${x1},${y1}  ${!isR ? lPath : rPath}  L${edge},${panelMY}`;
        };

        const getPath180To240 = (x1, y1, panelMY) => {
            const lPath = `L${x1},${!isUp ? bxEdge : y1} L${lxEdge},${
                !isUp ? bxEdge : y1
            } L${lxEdge},${panelMY}`;
            const rPath = `L${x1},${bnEdge} L${rnEdge},${bnEdge} L${rnEdge},${
                isUp ? panelMY : y1
            } L${rxEdge},${isUp ? panelMY : y1} L${rxEdge},${panelMY}`;

            return `M${x1},${y1}  ${!isR ? lPath : rPath}  L${edge},${panelMY}`;
        };

        const getPath240To300 = (x1, y1, panelMY) => {
            const lPath = `L${lnEdge},${y1} L${lnEdge},${panelMY}`;
            const rPath = `L${lnEdge},${y1} L${lnEdge},${bnEdge} L${rnEdge},${bnEdge} L${rnEdge},${panelMY}`;

            return `M${x1},${y1}  ${!isR ? lPath : rPath}  L${edge},${panelMY}`;
        };
        const getPath300To360 = (x1, y1, panelMY) => {
            const lPath = `L${x1},${tnEdge} L${lnEdge},${tnEdge} L${lnEdge},${panelMY}`;
            const rPath = `L${lnEdge},${y1} L${lnEdge},${bnEdge} L${rnEdge},${bnEdge} L${rnEdge},${panelMY}`;

            return `M${x1},${y1} ${!isR ? lPath : rPath}  L${edge},${panelMY}`;
        };

        if (centerAngle >= 0 && centerAngle <= 60) {
            return getPath0To60(x1, y1, panelMY);
        }
        if (centerAngle > 60 && centerAngle <= 120) {
            return getPath60To120(x1, y1, panelMY);
        }
        if (centerAngle > 120 && centerAngle <= 180) {
            return getPath120To180(x1, y1, panelMY);
        }
        if (centerAngle > 180 && centerAngle <= 240) {
            return getPath180To240(x1, y1, panelMY);
        }
        if (centerAngle > 240 && centerAngle <= 300) {
            return getPath240To300(x1, y1, panelMY);
        }
        if (centerAngle > 300 && centerAngle <= 360) {
            return getPath300To360(x1, y1, panelMY);
        }
    };

    return (
        <div className={styles.wrapper}>
            <OutsideClickHandler onOutsideClick={onOutsideClick}>
                <div className={styles.chartHeader}>
                    <div className={styles.title}>RM Risk Profile</div>
                    <div
                        className={styles.closeButton}
                        onClick={() => setVisibility(false)}
                    >
                        <img src={iconBlueCross} alt="close" />
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.svgWrapper}>
                        <svg
                            version="1.1"
                            id="pie_chart"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 800 350"
                        >
                            <symbol
                                id="p-chart"
                                viewBox="0 0 200 200"
                                width={200}
                                height={200}
                            >
                                <g>
                                    <motion.svg
                                        version="1.1"
                                        id="rm_pie_chart"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 200 200"
                                        className={styles.svg}
                                    >
                                        {sliceData &&
                                            sliceData.map((s, i) => (
                                                <motion.g
                                                    key={s.name}
                                                    custom={i}
                                                    initial={sliceOpt.initial(
                                                        s
                                                    )}
                                                    animate={sliceControls}
                                                >
                                                    <PieSlice
                                                        data={s}
                                                        key={s.name}
                                                        fill={s.fill}
                                                        score={s.score}
                                                        startAngle={s.start}
                                                        cssClass={`slice${i}`}
                                                    />
                                                </motion.g>
                                            ))}
                                    </motion.svg>
                                </g>
                            </symbol>

                            {sliceData &&
                                generatePanels(sliceData).map((c, i) => {
                                    const x = c.meta.x;
                                    const y = c.meta.isRightSide
                                        ? c.meta.y
                                        : c.meta.dy;

                                    let path = generateArrows(c, i);

                                    return (
                                        <motion.g
                                            key={`panel-${c.name}`}
                                            custom={i}
                                            initial={panelOpt.initial}
                                            animate={panelControls}
                                        >
                                            <path
                                                stroke="#549AB5"
                                                strokeWidth={0.8}
                                                d={path}
                                                fill="none"
                                                id={`line${c.name}`}
                                                className={`line${c.name} ${styles.lines}`}
                                                style={{ opacity: 1 }}
                                            />
                                            <SvgDetailPanel
                                                id={`panel-${c.name}`}
                                                data={c}
                                                x={x}
                                                y={y}
                                                height={c.meta.height}
                                                width={c.meta.width}
                                            />
                                        </motion.g>
                                    );
                                })}

                            <use href="#p-chart" x={300} y={75} />
                        </svg>
                    </div>
                </div>

                {chartData && (
                    <div className={styles.totalScore}>
                        {chartData.totalScore}
                    </div>
                )}
                <div className={styles.footer}>Total Risk Score</div>
            </OutsideClickHandler>
        </div>
    );
};

PieChart.propTypes = {
    chartData: PropTypes.shape({
        totalScore: PropTypes.number,
        moderateThreshold: PropTypes.number,
        majorThreshold: PropTypes.number
    }),
    sliceData: PropTypes.arrayOf(PropTypes.shape()),
    setVisibility: PropTypes.func
};
export default PieChart;
